function cssEscape(value) {
  if (value == null) return '';
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return String(value).replace(/(["'\\\[\]\(\)\.\#\:])/g, '\\$1');
}

// Used to derive thumbnail-size variants of the full-size URLs we store on swatch inputs + preload.
function resizeShopifyImageUrl(url, width) {
  if (!url) return url;
  if (/([?&])width=\d+/.test(url)) {
    return url.replace(/([?&])width=\d+/, '$1width=' + width);
  }
  return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
}

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this._otherGroupsLoaded = false;
    this._loadPromise = null;
    // Cache of section-rendering responses keyed by sorted option_value_ids.
    // Hover preloads write into it, click resolves read from it.
    this._sectionCache = new Map();
    this._sectionCacheLimit = 50;
    // Monotonic id used as a stale-guard: a click bumps the id; a response
    // with a stale id is ignored so it can't overwrite a newer selection.
    this._currentResolveId = 0;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl || window.location.pathname;
    this.drawer = this.querySelector('[data-variant-drawer]');
    this.hiddenId = this.querySelector('input[name="id"]');

    this._onChange = this._onChange.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onCancel = this._onCancel.bind(this);
    this._onPointerOver = this._onPointerOver.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);

    this.addEventListener('change', this._onChange);
    this.addEventListener('click', this._onClick);
    this.addEventListener('pointerover', this._onPointerOver);
    this.addEventListener('pointerdown', this._onPointerDown);

    if (this.drawer) {
      this.drawer.addEventListener('cancel', this._onCancel);
    }

    const initialInput = this.querySelector('input[data-option-name="Color"]:checked')
      || this.querySelector('input[type="radio"]:checked');
    if (initialInput) {
      const variant = this._readVariant(initialInput);
      this.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: variant, initial: true },
        bubbles: true,
      }));
    }

    // Eagerly warm the image cache for inline swatches (drawer swatches get
    // the same treatment in _openDrawer). Deferred to the next frame so the
    // initial paint isn't blocked by a batch of Image() fetches + decodes.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this._preloadInlineImages());
    } else {
      setTimeout(() => this._preloadInlineImages(), 0);
    }

    // Warm the Section Rendering cache for likely clicks. Without this, the
    // first click on any non-hovered swatch pays the full ~1–2s server
    // roundtrip; non-Fabric options (Finish/Color/Size) suffer most because
    // they're too small to benefit from typical hover-then-click patterns.
    // Deferred to idle so it doesn't compete with the image preload above.
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 400));
    idle(() => this._preloadInlineSections());
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('pointerover', this._onPointerOver);
    this.removeEventListener('pointerdown', this._onPointerDown);
    if (this.drawer) {
      this.drawer.removeEventListener('cancel', this._onCancel);
    }
  }

  // Capture the radio's checked state BEFORE the click flips it, so the
  // click handler can detect "user re-clicked the already-active swatch"
  // (no change event fires for that, but we still want the gallery to
  // re-sync — e.g. they navigated away and want the image back).
  _onPointerDown(e) {
    const swatchEl = e.target.closest('.swatch-element');
    if (!swatchEl || !this.contains(swatchEl)) {
      this._pointerDownInput = null;
      this._pointerDownChecked = false;
      return;
    }
    const input = swatchEl.querySelector(
      'input[type="radio"][data-option-name][data-option-value-id]'
    );
    this._pointerDownInput = input || null;
    this._pointerDownChecked = !!(input && input.checked);
  }

  _onPointerOver(e) {
    const swatchEl = e.target.closest('.swatch-element');
    if (!swatchEl || !this.contains(swatchEl)) return;
    const input = swatchEl.querySelector(
      'input[type="radio"][data-option-name][data-option-value-id]'
    );
    if (!input || input.checked) return;
    this._preloadFor(input);
    this._preloadImagesFor(input);
  }

  _preloadFor(input) {
    const optionName = input.dataset.optionName;
    const valueId = input.dataset.optionValueId;
    if (!optionName || !valueId) return;
    const key = this._buildSelectionKey(optionName, valueId);
    if (!key || this._sectionCache.has(key)) return;
    this._sectionCache.set(key, this._fetchSection(key));
    // Cap cache size — drop the oldest entry first (Map iteration is insertion order).
    if (this._sectionCache.size > this._sectionCacheLimit) {
      this._sectionCache.delete(this._sectionCache.keys().next().value);
    }
  }

  // Prime the browser's HTTP/image cache for the variant gallery images the
  // hovered swatch will need for quicker image updates.
  // We track preloaded URLs in a Set so repeated hovers over
  // the same swatch don't re-queue the same requests.
  _preloadImagesFor(input) {
    const swatchUrl = (input.getAttribute('data-metaobject-variant-swatch-image') || '').trim();
    if (swatchUrl) this._preloadOne(swatchUrl, true);
    const raw = input.getAttribute('data-metaobject-variant-specific-images') || '';
    raw.split(',').map((s) => s.trim()).filter(Boolean).forEach((u) => {
      this._preloadOne(u, true);
    });
  }

  // Single-URL preload + dedup. `decode` triggers off-main-thread bitmap
  // decoding so the eventual `mainImg.src = url` swap doesn't pay the decode
  // cost on click — only worth doing for the swatch image (the URL that
  // becomes the gallery's main src), not the secondary fabric_specific_images
  // which are only seen if the user explicitly clicks a thumb.
  _preloadOne(url, decode) {
    if (!url) return;
    if (!this._preloadedImages) this._preloadedImages = new Set();
    if (this._preloadedImages.has(url)) return;
    this._preloadedImages.add(url);
    const img = new Image();
    if ('fetchPriority' in img) img.fetchPriority = 'high';
    img.src = url;
    if (decode && typeof img.decode === 'function') {
      img.decode().catch(() => { return; });
    }
  }

  _buildSelectionKey(overrideOption, overrideValueId) {
    const selected = new Map();
    this.querySelectorAll(
      'input[data-option-name][data-option-value-id]:checked'
    ).forEach((input) => {
      const name = input.dataset.optionName;
      const id = input.dataset.optionValueId;
      if (name && id && !selected.has(name)) {
        selected.set(name, id);
      }
    });
    if (overrideOption && overrideValueId) {
      selected.set(overrideOption, overrideValueId);
    }
    if (selected.size === 0) return '';
    return Array.from(selected.values()).sort().join(',');
  }

  _fetchSection(key) {
    const url = this.productUrl
      + '?section_id=' + encodeURIComponent(this.sectionId)
      + '&option_values=' + encodeURIComponent(key);
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then((res) => (res.ok ? res.text() : null))
      .catch(() => null);
  }

  _beginLoading() {
    this._pendingFetches = (this._pendingFetches || 0) + 1;
    if (this._pendingFetches === 1) this._setLoadingState(true);
  }

  _endLoading() {
    this._pendingFetches = Math.max(0, (this._pendingFetches || 0) - 1);
    if (this._pendingFetches === 0) this._setLoadingState(false);
  }

  _setLoadingState(isLoading) {
    this.querySelectorAll('.variant-picker__more').forEach((btn) => {
      btn.classList.toggle('variant-picker__more--loading', isLoading);
      if (isLoading) {
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
      }
    });
  }

  _onCancel(e) {
    e.preventDefault();
    this._closeDrawer();
  }

  _lockBody() {
    if (document.body.dataset.variantDrawerLocked != null) return;
    if (window.matchMedia('(max-width: 768px)').matches) {
      const y = window.scrollY || window.pageYOffset || 0;
      document.body.dataset.variantDrawerLocked = String(y);
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + y + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.dataset.variantDrawerLocked = 'desktop';
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = scrollbarWidth + 'px';
      }
    }
  }

  _unlockBody() {
    const stored = document.body.dataset.variantDrawerLocked;
    if (stored == null) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    delete document.body.dataset.variantDrawerLocked;
    if (stored !== 'desktop') {
      window.scrollTo(0, parseInt(stored, 10) || 0);
    }
  }

  _onClick(e) {
    if (e.target.closest('[data-action="open-drawer"]')) {
      e.preventDefault();
      this._openDrawer();
      return;
    }
    if (e.target.closest('[data-action="close-extension"]')) {
      e.preventDefault();
      this._closeExtension();
      return;
    }
    if (e.target.closest('[data-action="close-drawer"]')) {
      e.preventDefault();
      this._closeDrawer();
      return;
    }
    if (e.target.closest('[data-drawer-backdrop]')) {
      e.preventDefault();
      this._closeDrawer();
      return;
    }

    // Re-click on the already-active swatch — the browser fires no change
    // event because the radio's state didn't move, so _applySelection
    // wouldn't run on its own. Re-run it to refresh the gallery, hidden
    // id, and ?variant= URL just as if the user had picked a fresh value.
    const swatchEl = e.target.closest('.swatch-element');
    if (swatchEl && this.contains(swatchEl)) {
      const input = swatchEl.querySelector(
        'input[type="radio"][data-option-name][data-option-value-id]'
      );
      if (input && input === this._pointerDownInput && this._pointerDownChecked) {
        this._applySelection(input);
      }
    }
    this._pointerDownInput = null;
    this._pointerDownChecked = false;
  }

  _onChange(e) {
    const input = e.target.closest('input[type="radio"]');
    if (!input || !this.contains(input)) return;
    this._applySelection(input);
  }

  _applySelection(input) {
    const optionName = input.dataset.optionName;
    const optionValue = input.value;
    const clickedFabric = input.dataset.fabricValue;

    if (clickedFabric) {
      const fabricInputs = this.querySelectorAll('input[data-option-name="Fabric"]');
      fabricInputs.forEach((fi) => { fi.checked = fi.value === clickedFabric; });
    }

    let targetFabric = clickedFabric;
    if (!targetFabric && optionName === 'Fabric') {
      targetFabric = optionValue;
    }
    if (!targetFabric) {
      const fabricInput = this.querySelector('input[data-option-name="Fabric"]:checked');
      if (fabricInput) targetFabric = fabricInput.value;
    }

    if (targetFabric) {
      const grouped = this.querySelectorAll('input[data-fabric-value]');
      grouped.forEach((g) => {
        if (g.dataset.fabricValue !== targetFabric) {
          g.checked = false;
        }
      });
    }

    if (optionName) {
      const siblings = this.querySelectorAll(
        'input[data-option-name="' + cssEscape(optionName) + '"][value="' + cssEscape(optionValue) + '"]'
      );
      siblings.forEach((s) => {
        const sFabric = s.dataset.fabricValue;
        if (!sFabric || sFabric === targetFabric) {
          s.checked = true;
        }
      });
    }

    if (this.drawer && this.drawer.contains(input)) {
      this._promoteToInline(input);
      this._openExtension(input);
    }

    this._syncVariant(input);

    // Always fetch so the OTHER options can have their availability /
    // data-variant-ids / drawer refreshed for the new combined selection
    // (combos that aren't possible should disappear / mark sold out).
    // The CLICKED option's DOM is always skipped in the swap — its own
    // swatches stay correct (non-clicked options haven't changed, so its
    // data-variant-ids are still valid) and the user just clicked into
    // that row, so we must not reshuffle it ("no swatches should move").
    // The loading spinner is only shown when the drawer-owner's UI is
    // actually being rebuilt (i.e. the clicked option is not the drawer
    // owner); otherwise nothing the user can see would animate and the
    // spinner would flash for no reason.
    const optionSwatch = input.closest('.swatch[data-option-name]');
    const clickedOptionName = optionSwatch ? optionSwatch.dataset.optionName : null;
    const ownsDrawer = !!(optionSwatch && optionSwatch.querySelector('[data-variant-drawer]'));
    this._resolveVariantId({
      skipOptionName: clickedOptionName,
      showLoading: !ownsDrawer,
    });
  }

  _resolveVariantId(opts) {
    // Ask the server to join the full current selection and give us back both
    // the authoritative variant id and the refreshed per-option swatch markup.
    // Hover preloads write into _sectionCache, so a click that follows a hover
    // for the same combo is effectively instant.
    const options = opts || {};
    const skipOptionName = options.skipOptionName || null;
    const allowLoading = options.showLoading !== false;

    const key = this._buildSelectionKey();
    if (!key) return;

    const resolveId = ++this._currentResolveId;

    // Keep a reference to the drawer node so we can re-read its hidden state
    // when the response arrives (the user may have closed it in the interim).
    const originalDrawer = this.drawer;
    const drawerId = originalDrawer ? originalDrawer.id : null;
    const openBody = originalDrawer ? originalDrawer.querySelector('.variant-drawer__body') : null;
    const drawerScroll = openBody ? openBody.scrollTop : 0;

    // Snapshot each to-be-swapped option's current inline order so we can
    // restore it after the DOM swap. Without this, the server's
    // "selected-first" rendering would move a selected swatch to position 0
    // of its inline row. The skipped (clicked) option isn't swapped, so no
    // capture is needed for it.
    const capturedInlineOrders = new Map();
    this.querySelectorAll('.swatch[data-option-name]').forEach((swatch) => {
      const optionName = swatch.dataset.optionName;
      if (!optionName) return;
      if (skipOptionName && optionName === skipOptionName) return;
      const inline = swatch.querySelector('.variant-picker__inline');
      if (!inline) return;
      const ids = [];
      inline.querySelectorAll('.swatch-element').forEach((el) => {
        const input = el.querySelector('input[data-option-value-id]');
        if (input && input.dataset.optionValueId) {
          ids.push(input.dataset.optionValueId);
        }
      });
      if (ids.length) capturedInlineOrders.set(optionName, ids);
    });

    let promise = this._sectionCache.get(key);
    const wasCached = !!promise;
    if (!promise) {
      promise = this._fetchSection(key);
      this._sectionCache.set(key, promise);
      if (this._sectionCache.size > this._sectionCacheLimit) {
        this._sectionCache.delete(this._sectionCache.keys().next().value);
      }
    }

    // Only show a loading state for fresh fetches that actually rebuild UI
    // the user is looking at. Cached responses resolve on the next microtask,
    // and clicks within a drawer-owning option don't re-render that option's
    // UI either — a spinner would just flash in both cases.
    const showLoading = allowLoading && !wasCached;
    if (showLoading) this._beginLoading();

    promise.then((text) => {
      if (showLoading) this._endLoading();

      // Stale-guard: a later resolve has superseded this one. Skip the swap
      // — but the cached response is still useful for next time.
      if (resolveId !== this._currentResolveId) return;
      if (!text) return;

      const doc = new DOMParser().parseFromString(text, 'text/html');

      // 1. Update the hidden variant id from the server's authoritative resolution.
      const resolved = doc.querySelector('variant-picker input[name="id"]')
        || doc.querySelector('input[name="id"]');
      if (resolved && resolved.value) {
        const hidden = this.querySelector('input[name="id"]');
        if (hidden) {
          hidden.value = resolved.value;
          hidden.setAttribute('value', resolved.value);
        }
        try {
          const fresh = new URL(window.location.href);
          fresh.searchParams.set('variant', resolved.value);
          window.history.replaceState({}, '', fresh.toString());
        } catch (err) {
          /* URL/History unavailable — skip silently */
        }
      }

      // 2. Refresh each option's markup EXCEPT the option the user clicked.
      //    The clicked option's own swatches/drawer don't need rebuilding
      //    (data-variant-ids already reflect the unchanged other options,
      //    and reshuffling its inline would violate the "no swatches move"
      //    rule). Every other option gets a full innerHTML swap so
      //    availability, data-variant-ids, drawer contents, and +N counts
      //    update to reflect the new combined selection.
      const newPicker = doc.querySelector('variant-picker');
      if (newPicker) {
        newPicker.querySelectorAll('.swatch[data-option-name]').forEach((newSwatch) => {
          const optionName = newSwatch.dataset.optionName;
          if (!optionName) return;
          if (skipOptionName && optionName === skipOptionName) return;

          const localSwatch = this.querySelector(
            '.swatch[data-option-name="' + cssEscape(optionName) + '"]'
          );
          if (!localSwatch) return;

          localSwatch.innerHTML = newSwatch.innerHTML;
        });
      }

      // 2a. Restore each swapped option's inline order to its pre-swap
      //     sequence. The swapped markup has the selected value at position 0;
      //     we move each previously-captured swatch back to its original spot
      //     (just before the +N button). Anything new the server introduced
      //     (values that became available under the new selection) is moved to
      //     the end so it doesn't displace captured swatches.
      capturedInlineOrders.forEach((ids, optionName) => {
        const inline = this.querySelector(
          '.swatch[data-option-name="' + cssEscape(optionName) + '"] .variant-picker__inline'
        );
        if (!inline) return;
        const moreButton = inline.querySelector('.variant-picker__more');
        const byId = new Map();
        inline.querySelectorAll('.swatch-element').forEach((el) => {
          const input = el.querySelector('input[data-option-value-id]');
          if (input && input.dataset.optionValueId) {
            byId.set(input.dataset.optionValueId, el);
          }
        });
        ids.forEach((id) => {
          const el = byId.get(id);
          if (!el) return;
          if (moreButton) {
            inline.insertBefore(el, moreButton);
          } else {
            inline.appendChild(el);
          }
          byId.delete(id);
        });
        byId.forEach((el) => {
          if (moreButton) {
            inline.insertBefore(el, moreButton);
          } else {
            inline.appendChild(el);
          }
        });
      });

      // 2b. Auto-select the first available swatch on any option whose
      //     previous value is no longer part of the new selection (e.g. the
      //     user changed Finish to a combo that drops the current Fabric).
      //     Without this the UI would just render the new option list with
      //     nothing checked. We prefer non-sold-out values and update the
      //     hidden id from the picked input's data-variant-id so the cart
      //     form still points at a valid variant.
      this.querySelectorAll('.swatch[data-option-name]').forEach((swatch) => {
        const optionName = swatch.dataset.optionName;
        if (skipOptionName && optionName === skipOptionName) return;
        const inline = swatch.querySelector('.variant-picker__inline');
        if (!inline) return;
        if (inline.querySelector('input[type="radio"]:checked')) return;

        const target = inline.querySelector(
          '.swatch-element:not(.soldout) input[type="radio"]'
        ) || inline.querySelector('input[type="radio"]');
        if (!target) return;
        target.checked = true;
        if (target.dataset.variantId) {
          const hidden = this.querySelector('input[name="id"]');
          if (hidden) {
            hidden.value = target.dataset.variantId;
            hidden.setAttribute('value', target.dataset.variantId);
          }
          try {
            const fresh = new URL(window.location.href);
            fresh.searchParams.set('variant', target.dataset.variantId);
            window.history.replaceState({}, '', fresh.toString());
          } catch (err) {
            /* URL/History unavailable — skip silently */
          }
        }
      });

      // 3. Re-query the drawer (its DOM was replaced) and restore open state
      //    only if the user hasn't closed it during the fetch. Re-reading
      //    hasAttribute('hidden') on the original node right before the swap
      //    catches any close that happened while we were waiting on the wire.
      const drawerStillOpen = !!(originalDrawer && originalDrawer.open);
      this.drawer = this.querySelector('[data-variant-drawer]');
      if (drawerStillOpen && this.drawer && this.drawer.id === drawerId) {
        if (typeof this.drawer.showModal === 'function' && !this.drawer.open) {
          this.drawer.showModal();
        }
        this.drawer.classList.add('variant-drawer--open');
        this.drawer.addEventListener('cancel', this._onCancel);
        const newBody = this.drawer.querySelector('.variant-drawer__body');
        if (newBody) newBody.scrollTop = drawerScroll;
      }

      // 4. Re-sync the gallery off the post-swap DOM. The newly-swapped Fabric
      //    swatch may carry different data-metaobject-variant-specific-images
      //    than the one we read optimistically in _syncVariant, so the thumb
      //    strip + main image need to match the authoritative state.
      this._syncGallery(null);

      // 5. Re-fire variant:change so updateVariantUI picks up the authoritative
      //    availability/price/inventory. The optimistic dispatch in
      //    _syncVariant ran against the clicked swatch's data-attrs, but the
      //    section swap (and any auto-select fallback) may have moved us to
      //    a different combined variant — sold-out status especially.
      const authoritative = this.querySelector(
        'input[data-option-name][data-option-value-id]:checked'
      );
      if (authoritative) {
        this.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant: this._readVariant(authoritative) },
          bubbles: true,
        }));
      }
    });
  }

  _promoteToInline(drawerInput) {
    const optionName = drawerInput.dataset.optionName;
    const optionValueId = drawerInput.dataset.optionValueId;
    if (!optionName || !optionValueId) return;

    // Scope to the matching option's swatch wrapper.
    const optionSwatch = this.querySelector(
      '.swatch[data-option-name="' + cssEscape(optionName) + '"]'
    );
    if (!optionSwatch) return;
    const inlineContainer = optionSwatch.querySelector('.variant-picker__inline');
    if (!inlineContainer) return;

    const firstSwatch = inlineContainer.querySelector('.swatch-element');
    if (!firstSwatch) return;

    // Skip if the picked value already occupies position 0 — no work to do.
    const firstInput = firstSwatch.querySelector('input[type="radio"]');
    if (firstInput && firstInput.dataset.optionValueId === optionValueId) return;

    const drawerSwatch = drawerInput.closest('.swatch-element');
    if (!drawerSwatch) return;

    const clone = drawerSwatch.cloneNode(true);
    const clonedInput = clone.querySelector('input[type="radio"]');
    if (!clonedInput) return;

    clonedInput.id = 'swatch-inline-promoted-' + optionValueId;
    clonedInput.name = clonedInput.dataset.optionName || clonedInput.name;
    clonedInput.removeAttribute('data-fabric-value');
    clonedInput.removeAttribute('data-fabric-option-value-id');
    clonedInput.checked = true;

    const clonedLabel = clone.querySelector('label');
    if (clonedLabel) clonedLabel.setAttribute('for', clonedInput.id);

    // Strip drawer-only decorations so the clone matches inline rendering.
    Array.from(clone.children).forEach((child) => {
      const tag = child.tagName;
      if (tag !== 'INPUT' && tag !== 'LABEL') {
        child.remove();
      }
    });

    // Replace the first inline swatch in place — no shift, no trim. The other
    // 9 positions stay exactly where the user last saw them.
    firstSwatch.replaceWith(clone);
  }

  _syncVariant(input) {
    const variantId = input.dataset.variantId;

    // Query fresh in case the form DOM was reshuffled (the mobile layout
    // unwrap/rewrap script moves containers around on resize).
    const hiddenId = this.querySelector('input[name="id"]');
    if (hiddenId && variantId) {
      hiddenId.value = variantId;
      hiddenId.setAttribute('value', variantId);
    }

    if (variantId) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (err) {
        /* URL/History unavailable — skip silently */
      }
    }

    // Update every option's selected-value label. Each `[data-selected-label]`
    // lives inside a `[data-option-name]` swatch wrapper, so we resolve the
    // option per label and write its currently-checked input's value.
    this.querySelectorAll('[data-selected-label]').forEach((label) => {
      const swatch = label.closest('[data-option-name]');
      if (!swatch) return;
      const optionName = swatch.dataset.optionName;
      const checked = this.querySelector(
        'input[data-option-name="' + cssEscape(optionName) + '"]:checked'
      );
      label.textContent = checked ? checked.value : '';
    });

    const variant = this._readVariant(input);
    this.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant: variant },
      bubbles: true,
    }));

    // Rebuild the gallery (variant-specific thumbs + main image) in the same
    // tick as the variant:change dispatch so listeners that update price/
    // inventory and the gallery swap commit together
    this._syncGallery(variant);
  }

  _readVariant(input) {
    if (!input.dataset.variantId) return null;
    const image = input.dataset.variantImage || null;
    return {
      id: parseInt(input.dataset.variantId, 10),
      available: input.dataset.variantAvailable === 'true',
      price: input.dataset.variantPrice ? parseInt(input.dataset.variantPrice, 10) : null,
      compare_at_price: input.dataset.variantCompareAtPrice
        ? parseInt(input.dataset.variantCompareAtPrice, 10)
        : null,
      title: input.dataset.variantTitle || '',
      image: image ? { src: image, id: input.dataset.variantImageId || null } : null,
      featured_image: image ? { src: image } : null,
      inventory_management: input.dataset.variantInventoryManagement || null,
      inventory_quantity: input.dataset.variantInventory
        ? parseInt(input.dataset.variantInventory, 10)
        : 0,
    };
  }

  _openDrawer() {
    if (!this.drawer) return;
    if (typeof this.drawer.showModal === 'function' && !this.drawer.open) {
      this.drawer.showModal();
    }
    this._lockBody();
    void this.drawer.offsetWidth;
    this.drawer.classList.add('variant-drawer--open');
    this._loadOtherGroups();
    // Eagerly warm the image cache for every swatch the user can now reach.
    // Hovering still primes individual swatches, but this covers fast taps
    // (mobile) and keyboard-style clicks where the user doesn't linger first.
    // Deferred to the next tick so the drawer paints before we kick off a
    // large batch of Image() fetches — browser concurrency caps handle the
    // rest naturally.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => this._preloadDrawerImages());
    } else {
      setTimeout(() => this._preloadDrawerImages(), 0);
    }

    // Desktop: if the active swatch carries a drawer-description template,
    // slide the extension out alongside the drawer. Delayed by the drawer
    // transition (320ms) so the drawer settles into place first, then the
    // extension emerges from behind it — otherwise the extension's longer
    // sweep makes it briefly peek out to the left of the still-arriving
    // drawer. Mobile keeps the bottom-sheet hidden until an explicit tap.
    if (window.matchMedia('(min-width: 769px)').matches) {
      const checked = this.drawer.querySelector(
        '.swatch-element input[type="radio"]:checked'
      );
      const swatchEl = checked ? checked.closest('.swatch-element') : null;
      if (swatchEl && swatchEl.querySelector('template[data-drawer-description]')) {
        setTimeout(() => {
          if (this.drawer && this.drawer.classList.contains('variant-drawer--open')) {
            this._openExtension(checked);
          }
        }, 320);
      }
    }
  }

  _preloadDrawerImages() {
    if (!this.drawer) return;
    this.drawer.querySelectorAll('.swatch-element').forEach((swatchEl) => {
      const input = swatchEl.querySelector(
        'input[type="radio"][data-option-name][data-option-value-id]'
      );
      if (input) this._preloadImagesFor(input);
    });
  }

  // Two-pass preload for the inline row. The browser caps concurrent fetches
  // per origin (~6), so naively warming every swatch's swatch image AND every
  // fabric_specific_image lets thumb-only assets queue ahead of swatch images
  // the user is more likely to click. Pass 1 warms only the swatch image of
  // each inline swatch (this is the URL that becomes the gallery's main src
  // on click). Pass 2 — deferred to idle — warms the secondary thumbs.
  _preloadInlineImages() {
    const inputs = [];
    this.querySelectorAll('.variant-picker__inline .swatch-element').forEach((swatchEl) => {
      const input = swatchEl.querySelector(
        'input[type="radio"][data-option-name][data-option-value-id]'
      );
      if (input) inputs.push(input);
    });

    inputs.forEach((input) => {
      const url = (input.getAttribute('data-metaobject-variant-swatch-image') || '').trim();
      if (url) this._preloadOne(url, true);
    });

    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idle(() => {
      inputs.forEach((input) => {
        const raw = input.getAttribute('data-metaobject-variant-specific-images') || '';
        raw.split(',').map((s) => s.trim()).filter(Boolean).forEach((u) => {
          this._preloadOne(u, false);
        });
      });
    });
  }

  // Hover-style section-render preload, but kicked off at init for every
  // inline swatch so direct clicks (no hover) hit the cache. The currently-
  // checked value is included too so a click → click-elsewhere → click-back
  // round-trip stays instant on the return leg. _preloadFor handles dedup
  // against `_sectionCache`, so values that the user later hovers won't
  // re-fetch.
  _preloadInlineSections() {
    this.querySelectorAll('.variant-picker__inline .swatch-element').forEach((swatchEl) => {
      const input = swatchEl.querySelector(
        'input[type="radio"][data-option-name][data-option-value-id]'
      );
      if (!input) return;
      this._preloadFor(input);
    });
  }

  // Rebuild the variant-specific thumb strip and swap the main gallery image
  // in one synchronous pass so thumbs + main flip together. Reads fresh URLs
  // off the currently-checked Fabric swatch (swatch image first, then the
  // fabric metaobject's fabric_specific_images). When no variant-specific
  // images exist, falls back to swapping to the product.image thumb that
  // matches `variant.image.id` (passed through from _readVariant).
  _syncGallery(variant) {
    const thumbsContainer = document.querySelector('#product .product-main-images .thumbs');
    if (!thumbsContainer) return;

    // 1. Collect URLs off the currently-checked Fabric swatch.
    const urls = [];
    const source = this.querySelector(
      'input[data-option-name="Fabric"]:checked[data-metaobject-variant-specific-images], '
      + 'input[data-option-name="Fabric"]:checked[data-metaobject-variant-swatch-image]'
    ) || this.querySelector(
      'input[type="radio"]:checked[data-metaobject-variant-specific-images], '
      + 'input[type="radio"]:checked[data-metaobject-variant-swatch-image]'
    );
    if (source) {
      // Specific images go first so the gallery leads with the lifestyle/
      // detail shots of this variant, with the flat swatch tile pushed to
      // the end. The first URL in this list also becomes the main image
      // (the first variant-specific thumb is what _syncGallery promotes).
      const raw = source.getAttribute('data-metaobject-variant-specific-images') || '';
      raw.split(',').map((s) => s.trim()).filter(Boolean).forEach((u) => urls.push(u));
      const swatchUrl = (source.getAttribute('data-metaobject-variant-swatch-image') || '').trim();
      if (swatchUrl) urls.push(swatchUrl);
    }

    // Dedupe — parent_variant_swatches merchants often reuse the same
    // asset for the swatch tile AND the variant featured image, which
    // would otherwise render two identical thumbs side-by-side.
    const uniqueUrls = Array.from(new Set(urls));

    // Idempotency guard: if the strip already carries exactly these
    // variant-specific thumbs in this order, the desired state matches
    // reality and there's nothing to do. Bailing out preserves whatever
    // the user did between calls — most notably a thumb click that
    // moved `active` to a product-image thumb (see step 4 in
    // _resolveVariantId, which runs a second _syncGallery after the
    // section fetch resolves). Without this, that call would reset the
    // `active` state and clobber the user's selection.
    const existingRels = Array.from(
      thumbsContainer.querySelectorAll('[data-variant-specific="true"]')
    ).map((el) => el.getAttribute('rel'));
    if (existingRels.length === uniqueUrls.length
      && existingRels.every((rel, i) => rel === uniqueUrls[i])) {
      return;
    }

    // 2. Build the new thumbs off-DOM first
    let fragment = null;
    if (uniqueUrls.length) {
      fragment = document.createDocumentFragment();
      uniqueUrls.forEach((fullUrl) => {
        const span = document.createElement('span');
        span.className = 'thumb';
        span.setAttribute('data-variant-specific', 'true');
        span.setAttribute('rel', fullUrl);

        const img = document.createElement('img');
        img.src = resizeShopifyImageUrl(fullUrl, 120);
        img.loading = 'eager';
        span.appendChild(img);

        fragment.appendChild(span);
      });
    }

    // 3. Drop old variant-specific thumbs and insert the new ones. Then pick
    //    the target to promote via swapProductImage — either the first new
    //    variant thumb or, for variants without any fabric metafield images,
    //    a product.image thumb that matches `variant.image.id`.
    thumbsContainer.querySelectorAll('[data-variant-specific="true"]').forEach((el) => {
      el.remove();
    });

    if (fragment) {
      thumbsContainer.insertBefore(fragment, thumbsContainer.firstChild);
    }

    let target = null;
    if (fragment) {
      target = thumbsContainer.querySelector('[data-variant-specific="true"]');
    } else if (variant && variant.image && variant.image.id) {
      target = thumbsContainer.querySelector(
        '.thumb[data-image-id="' + variant.image.id + '"]'
      );
    }
    // No swatch image and no variant.image to promote — the previous fabric's
    // hero would otherwise stay pinned in the main slot. Reset to the first
    // non-variant-specific thumb (the product default).
    if (!target && !fragment) {
      target = thumbsContainer.querySelector('.thumb:not([data-variant-specific="true"])');
    }
    if (!target || target.classList.contains('active')) return;

    const src = target.getAttribute('rel');
    if (!src) return;

    // Inlined equivalent of scripts.js.liquid's swapProductImage — kept in
    // sync with that function's behaviour (no srcset, main src = rel, zoom
    // href updated, doHoverZoom re-bound if present).
    const currentActive = thumbsContainer.querySelector('.thumb.active');
    if (currentActive) currentActive.classList.remove('active');
    target.classList.add('active');

    const mainImg = document.querySelector('#product .images .main img');
    if (mainImg) {
      mainImg.setAttribute('src', src);
      mainImg.removeAttribute('srcset');
    }

    const zoomLink = document.querySelector('#product .images .main .zoom');
    if (zoomLink) zoomLink.setAttribute('href', src);

    if (typeof window.doHoverZoom === 'function') window.doHoverZoom();
  }

  _closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('variant-drawer--open');
    this._closeExtension();
    setTimeout(() => {
      if (this.drawer
        && !this.drawer.classList.contains('variant-drawer--open')
        && this.drawer.open) {
        this.drawer.close();
        this._unlockBody();
      }
    }, 320);
  }

  // Slide the secondary panel out from behind the drawer with the clicked
  // swatch's drawer_content description. Drawer-only — inline swatches don't
  // trigger this. Skipped silently when the swatch has no description (no
  // <template data-drawer-description> in its DOM).
  _openExtension(input) {
    if (!this.drawer) return;
    const extension = this.drawer.querySelector('[data-drawer-extension]');
    if (!extension) return;
    const content = extension.querySelector('[data-extension-content]');
    if (!content) return;

    const swatchEl = input.closest('.swatch-element');
    const template = swatchEl
      ? swatchEl.querySelector('template[data-drawer-description]')
      : null;
    if (!template) {
      // Swatch has no drawer_content — close any panel left over from a prior
      // click rather than leaving stale content visible.
      this._closeExtension();
      return;
    }

    content.innerHTML = '';
    content.appendChild(template.content.cloneNode(true));

    if (extension.hasAttribute('hidden')) {
      extension.removeAttribute('hidden');
      extension.setAttribute('aria-hidden', 'false');
      // Force reflow so the transition fires from the just-removed `hidden`
      // state rather than skipping straight to the open transform.
      void extension.offsetWidth;
    }
    extension.classList.add('variant-drawer__extension--open');
  }

  _closeExtension() {
    if (!this.drawer) return;
    const extension = this.drawer.querySelector('[data-drawer-extension]');
    if (!extension) return;
    if (extension.hasAttribute('hidden')) return;
    extension.classList.remove('variant-drawer__extension--open');
    extension.setAttribute('aria-hidden', 'true');
    // Wait for the slide-out to finish before applying `hidden` (which sets
    // display: none) so the panel animates out instead of vanishing.
    setTimeout(() => {
      if (!extension.classList.contains('variant-drawer__extension--open')) {
        extension.setAttribute('hidden', '');
      }
    }, 320);
  }

  _loadOtherGroups() {
    if (this._otherGroupsLoaded) return Promise.resolve();
    if (this._loadPromise) return this._loadPromise;

    const pending = Array.from(this.drawer.querySelectorAll('[data-fabric-group][data-loading]'));
    if (!pending.length) {
      this._otherGroupsLoaded = true;
      return Promise.resolve();
    }

    this._loadPromise = Promise.all(pending.map((group) => this._fetchGroup(group)))
      .then(() => { this._otherGroupsLoaded = true; })
      .catch((err) => { console.error('variant-picker: failed to load drawer groups', err); })
      .then(() => { this._loadPromise = null; });

    return this._loadPromise;
  }

  async _fetchGroup(group) {
    const optionValueId = group.dataset.fabricOptionValueId;
    const fabricValue = group.dataset.fabricGroup;
    if (!optionValueId) return;

    const url = this.productUrl
      + '?section_id=' + encodeURIComponent(this.sectionId)
      + '&option_values=' + encodeURIComponent(optionValueId);

    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) return;
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const source = doc.querySelector(
      '[data-fabric-group="' + cssEscape(fabricValue) + '"]'
    );
    if (!source) return;
    group.innerHTML = source.innerHTML;
    group.removeAttribute('data-loading');
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
